/**
 * SoleadLab 2.0 - Data Point Card (Compact)
 *
 * Compact single-row layout for monitoring data points.
 * Shows status dot, name, entity ID, value, alarm state, and timestamp inline.
 */

import { useMonitoringStore } from '@/stores';
import { useUIStore } from '@/stores';
import { useIsReadOnlyViewer } from '@/hooks/useShareViewer';
import type { DataPointConfig, DataPointRuntimeState, AlarmState } from '@/types/monitoring';
import './DataPointCard.css';

interface DataPointCardProps {
  config: DataPointConfig;
  runtime?: DataPointRuntimeState;
}

const STATUS_LABELS: Record<AlarmState, string> = {
  ok: 'OK',
  warning: 'WARNING',
  critical: 'CRITICAL',
  stale: 'STALE',
  no_data: 'NO DATA',
};

export default function DataPointCard({ config, runtime }: DataPointCardProps) {
  // F-02E-2C: share viewers see the card display (name / entity / value
  // / state / time) but no Edit/Delete hover actions and no click-to-edit
  // affordance. The backend rejects the mutations anyway; this gate keeps
  // the UI from hinting at them.
  const isReadOnly = useIsReadOnlyViewer();
  const removeDataPointConfig = useMonitoringStore((s) => s.removeDataPointConfig);
  const openModal = useUIStore((s) => s.openModal);
  const showConfirm = useUIStore((s) => s.showConfirm);
  const addNotification = useUIStore((s) => s.addNotification);

  const state = runtime?.state ?? 'no_data';
  const values = runtime?.values ?? {};
  const triggeredCount = (runtime?.triggered_rules?.length ?? 0) + (runtime?.triggered_groups?.length ?? 0);

  const primaryMetric = config.selected_metrics[0];
  const primaryValue = primaryMetric ? values[primaryMetric] : null;

  const handleEdit = () => {
    openModal('editDataPoint', { datapointId: config.id });
  };

  const handleDelete = () => {
    showConfirm({
      title: 'Delete data point',
      message: `Delete the data point "${config.display_name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
      // B3a — canonical DELETE; notify on failure (conflict/error surfaced from the store).
      onConfirm: async () => {
        const ok = await removeDataPointConfig(config.id);
        if (!ok) addNotification({ type: 'error', title: 'Could not delete data point', message: useMonitoringStore.getState().configError ?? 'Please retry.' });
      },
    });
  };

  return (
    <div
      className={`dp-card dp-card--${state}`}
      onClick={isReadOnly ? undefined : handleEdit}
      style={isReadOnly ? { cursor: 'default' } : undefined}
    >
      {/* Row 1: status dot + name + entity + value + state + time */}
      <div className="dp-card__row">
        <span className={`dp-card__status-dot dp-card__status-dot--${state}`} />
        <span className="dp-card__name" title={config.display_name}>
          {config.display_name}
        </span>
        {triggeredCount > 0 && (
          <span className={`dp-card__alarm-badge dp-card__alarm-badge--${state}`}>
            {triggeredCount}
          </span>
        )}
      </div>

      {/* Row 2: entity + value + state + time */}
      <div className="dp-card__row dp-card__row--detail">
        <span className="dp-card__entity">{config.entity_id}</span>
        <span className="dp-card__value">
          {primaryValue !== null && primaryValue !== undefined ? (
            <>
              <span className="dp-card__value-num">
                {typeof primaryValue === 'number'
                  ? primaryValue.toFixed(config.decimals ?? 2)
                  : String(primaryValue)}
              </span>
              {config.unit && <span className="dp-card__value-unit"> {config.unit}</span>}
            </>
          ) : (
            <span className="dp-card__value-na">—</span>
          )}
        </span>
        <span className={`dp-card__state dp-card__state--${state}`}>
          {STATUS_LABELS[state]}
        </span>
        {runtime?.ts && (
          <span className="dp-card__time" title={runtime.ts}>
            {new Date(runtime.ts).toLocaleTimeString()}
          </span>
        )}
      </div>

      {/* Hover actions — owners only. Share viewers see no mutation hint. */}
      {!isReadOnly && (
        <div
          className="dp-card__actions"
          onClick={(e) => e.stopPropagation()}
          data-testid="dp-card-actions"
        >
          <button className="dp-card__action" onClick={handleEdit} title="Edit">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button className="dp-card__action dp-card__action--danger" onClick={handleDelete} title="Delete">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
